import React, { useState, useEffect } from 'react';
import { Dropdown, Badge, Button } from 'react-bootstrap';
import axios from 'axios';
import { API_URL, APP_URL } from '../../constants';
import { useTokens } from '../../context/TokenContext';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// Helper function to format date
const formatNotificationDate = (dateString) => {
  const date = new Date(dateString);
  const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  return date.toLocaleDateString(undefined, options);
};

const NotificationBar = ({ userToken, NotificationList, totalNotificationCount, userRole = 'admin' }) => {
  const [notifications, setNotifications] = useState(NotificationList.data || []);
  const [unreadCount, setUnreadCount] = useState(totalNotificationCount);
  const [open, setOpen] = useState(false);
  const { setItem } = useTokens();

  // Update notifications and unread count when NotificationList prop changes
  useEffect(() => {
    if (totalNotificationCount - unreadCount > 0) {
      const difference = totalNotificationCount - unreadCount;
      toast.info(`You have ${difference} new notifications.`, {
        autoClose: 4000, 
        hideProgressBar: true, 
        closeButton: true, 
        pauseOnHover: true, 
        draggable: true, 
        theme: "dark" 
      });
    }
    
    const sortedNotifications = (NotificationList.data || []).sort((a, b) => {
      // Parse dates to compare correctly
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB.getTime() - dateA.getTime(); // Descending order
    });

    setNotifications(sortedNotifications);
    const count = (NotificationList.data || []).filter(notif => !notif.isRead).length;
    setUnreadCount(totalNotificationCount);
  }, [unreadCount, totalNotificationCount]);

  const handleToggle = (isOpen) => {
    setOpen(isOpen);
  };

  const handleNotificationClick = async (notificationId) => {
    const notificationToMark = notifications.find(n => n.id === notificationId);
    if (notificationToMark && !notificationToMark.isRead) {
      try {
        await axios.post(`${API_URL}/notification/read/${notificationId}`, {}, {
          headers: {
            Authorization: `Bearer ${userToken}`
          }
        });
        // Optimistically update the UI for the specific notification
        setNotifications(prevNotifications =>
          prevNotifications.map(notif =>
            notif.id === notificationId ? { ...notif, isRead: true } : notif
          )
        );
        setUnreadCount(prevCount => {
          const newCount = Math.max(0, prevCount - 1);
          setItem('admin', 'UserNotifications', {
            notifications: notifications.map(notif =>
              notif.id === notificationId ? { ...notif, isRead: true } : notif
            ),
            unreadCount: newCount
          });
          return newCount;
        });
      } catch (error) {
        console.error(`Error marking notification ${notificationId} as read:`, error);
        // Handle error, e.g., show a toast notification
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount > 0) {
      try {
        await axios.post(`${API_URL}/notification/read-all`, {}, {
          headers: {
            Authorization: `Bearer ${userToken}`
          }
        });
        // Optimistically update all notifications as read
        setNotifications(prevNotifications =>
          prevNotifications.map(notif => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
        // Handle error
      }
    }
  };
  
  const generateNotificationLink = (notification) => {
    let meta = {};
    if (notification.meta) {
      try {
        meta = typeof notification.meta === 'object' ? notification.meta : JSON.parse(notification.meta);
      } catch (e) {
        meta = {};
      }
    }

    switch (notification.type) {
      case 'order':
        if (meta.orderId) {
          // This path depends on whether it's an admin or vendor order
          // Assuming vendor notifications with type 'order' go to vendor panel
          // and admin_alert type with 'order' meta would go to admin order update.
          // Since your data is for a user_id:18 (vendor), let's assume this is for vendor.
          return `${APP_URL}vendor/order/update/${meta.orderId}`;
        }
        break;
      case 'product':
        if (meta.productId) {
          // Assuming product approval notifications are for vendor
          return `${APP_URL}vendor/product/edit/${meta.productId}`;
        }
        break;
      case 'admin_alert':
        if (meta.vendorId && notification.title.includes('New Vendor Registration')) {
          return `${APP_URL}admin/vendor/edit/${meta.vendorId}`;
        } else if (meta.customerId && notification.title.includes('New Customer Registered')) {
          return `${APP_URL}admin/customer/edit/${meta.customerId}`;
        } else if (meta.productId && notification.title.includes('New Product Pending Approval')) {
          return `${APP_URL}admin/product/edit/${meta.productId}`;
        } else if (meta.ticketId && notification.title.includes('New Support Ticket Received')) {
          return `${APP_URL}admin/support/edit/${meta.ticketId}`;
        } else if (meta.payoutId && notification.title.includes('New Payout Request')) {
          return `${APP_URL}admin/payout/edit/${meta.payoutId}`;
        }
        break;
      case 'payout': // Assuming this is for vendor payout status update
        if (meta.payoutId) {
          return `${APP_URL}vendor/payout/view/${meta.payoutId}`; // Assuming a view page for vendor payouts
        }
        break;
      case 'support': // Assuming this is for vendor support ticket reply
        if (meta.ticketId) {
          return `${APP_URL}vendor/support/edit/${meta.ticketId}`;
        }
        break;
      // Add more cases for other notification types if needed
      // case 'account': // For account approvals
      //   return `${APP_URL}user/profile`; // Or a specific dashboard link
    }
    // Fallback to a generic notifications page or null if no specific link
    return `${APP_URL}notifications`;
  };

  const getNotificationIcon = (notification) => {
    const type = notification.type;
    const title = notification.title;

    switch (type) {
      case 'order':
        return 'receipt'; // For new orders
      case 'product':
        return 'box-seam'; // For product approvals
      case 'payout':
        return 'cash-coin'; // For payout requests/updates
      case 'support':
        return 'life-preserver'; // For support tickets
      case 'account':
        return 'person-circle'; // For account-related
      case 'admin_alert':
        if (title.includes('Vendor Registration') || title.includes('Customer Registered')) {
          return 'person-plus';
        } else if (title.includes('Product Pending Approval')) {
          return 'box-seam';
        } else if (title.includes('Payout Request')) {
          return 'cash-coin';
        } else if (title.includes('Support Ticket Received')) {
          return 'ticket-perforated';
        }
        return 'bell'; // General admin alert
      default:
        return 'info-circle'; // Default icon
    }
  };

  return (
    <Dropdown onToggle={handleToggle} show={open} align="end" className="notificationDropDown">
      <Dropdown.Toggle as="div" id="dropdown-notifications" className="d-flex align-items-center cursor-pointer position-relative">
        {/* Bootstrap Icon for the bell */}
        <i className="bi bi-bell-fill fs-6 text-secondary notification-bell-icon"></i> {/* Adjusted icon size to fs-4 and text-secondary */}
        {unreadCount > 0 && (
          <Badge bg="danger" pill className="notification-badge-custom position-absolute translate-middle">
            {unreadCount}
          </Badge>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="shadow bg-white">
        <h6 className="dropdown-header d-flex justify-content-between align-items-center">
          <span className="text-dark">Notifications</span>
          <small className="text-muted-xss">
            {unreadCount > 0 ? `${unreadCount} unread` : 'No new notifications'}
          </small>
        </h6>
        {/* <Dropdown.Divider /> */}

        {/* Mark All As Read Button */}
        {unreadCount > 0 && (
          <Dropdown.Item onClick={handleMarkAllAsRead} className="text-center text-primary fw-bold">
            Mark all as read
          </Dropdown.Item>
        )}
        {/* {unreadCount > 0 && notifications.length > 0 && <Dropdown.Divider />} */}

        {notifications.length > 0 ? (
          notifications.map(notification => (
            <Dropdown.Item
              key={notification.id}
              // Removed onClick from the entire item to avoid accidental navigation or marking read on content click
              // The handleNotificationClick is now triggered only when the "View" button is clicked OR
              // you can add it back here if you want any click on the item to mark it read,
              // but if the user just wants to glance, it's better to only mark on View or specific action.
              // For now, I'm keeping the original handleNotificationClick on the item itself.
              onClick={() => handleNotificationClick(notification.id)}
              className={`notification-item ${notification.is_read === '0' ? 'notification-unread' : 'notification-read'}`}
            >
              <div className="d-flex align-items-center mb-1">
                <i className={`bi bi-${getNotificationIcon(notification)} me-2 fs-5 text-primary notification-item-icon`}></i>
                <div className="flex-grow-1">
                  <h6 className={`mb-0 ${notification.is_read === '0' ? 'fw-bold text-dark' : 'text-secondary'}`}>{notification.title}</h6>
                </div>
              </div>
              {/* Removed the `Link` component from wrapping the entire content */}
              <p className={`text-muted ${notification.is_read === '0' ? 'text-dark' : ''}`}>{notification.content}</p>
              <div className="d-flex justify-content-between align-items-end">
                <small className="text-muted-xss">{formatNotificationDate(notification.created_at)}</small>
                {/* View Button/Link */}
                <Link to={generateNotificationLink(notification)}
                  className="text-info py-0 px-2 small-xss-btn btn btn-link" // Added btn btn-link to make it look like a button but still navigate
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent dropdown item's onClick from firing
                    setOpen(false); // Close dropdown on link click
                    handleNotificationClick(notification.id); // Mark as read when "View" is clicked
                  }}
                >
                  View <i className="bi bi-box-arrow-up-right ms-1"></i>
                </Link>
              </div>
            </Dropdown.Item>
          ))
        ) : (
          <Dropdown.Item text className="text-center text-muted">
            No notifications to display.
          </Dropdown.Item>
        )}

        <Dropdown.Divider />
        <Dropdown.Item
          href={`${APP_URL}${userRole}/notifications`} // Redirect to full notifications page
          className="text-center text-primary fw-bold"
          onClick={() => setOpen(false)} // Close dropdown when redirecting
        >
          View All Notifications
          <i className={`bi bi-chevron-${open ? 'down' : 'right'} ms-1`}></i> {/* Bootstrap Chevron icons */}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationBar;